import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Search, User, XCircle, Calendar, Star, Wallet, BadgeCheck, Phone, Mail, StickyNote, Trash2, Send, CornerDownRight, AtSign } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-ZM", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};
const money = (n) => `K${Number(n || 0).toLocaleString("en-ZM", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const paymentClass = (s) => ({
  paid: "bg-green-100 text-green-800",
  refunded: "bg-slate-100 text-slate-700",
  failed: "bg-rose-100 text-rose-700",
  unpaid: "bg-amber-100 text-amber-800",
}[s || "unpaid"] || "bg-slate-100 text-slate-700");
const statusClass = (s) => ({
  confirmed: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-rose-100 text-rose-700",
  pending: "bg-amber-100 text-amber-800",
  refunded: "bg-slate-100 text-slate-700",
}[s || "pending"] || "bg-slate-100 text-slate-700");

export default function AdminGuestLookup() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const guestUserId = result?.guest?.user_id;

  const runSearch = useCallback(async () => {
    const q = query.trim();
    if (q.length < 3) {
      toast.error("Enter at least 3 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/guests/lookup`, { params: { q } });
      setResult(data);
      setNotes(data.admin_notes || []);
      if (!data.guest && data.bookings.length === 0) {
        toast.info("No matching guest, booking or review found");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  }, [query]);

  const reloadNotes = useCallback(async () => {
    if (!guestUserId) return;
    try {
      const { data } = await axios.get(`${API}/admin/guests/${guestUserId}/notes`);
      setNotes(data);
    } catch { /* silent */ }
  }, [guestUserId]);
  useEffect(() => { if (guestUserId) reloadNotes(); }, [guestUserId, reloadNotes]);

  const addNote = async (text, parentId = null) => {
    const body = (text || "").trim();
    if (!body) { toast.error("Please write a note first"); return; }
    if (!guestUserId) { toast.error("No guest selected"); return; }
    setAddingNote(true);
    try {
      await axios.post(`${API}/admin/guests/${guestUserId}/notes`, {
        note: body,
        ...(parentId ? { parent_id: parentId } : {}),
      });
      if (!parentId) setNoteInput("");
      toast.success(parentId ? "Reply posted" : "Note added");
      reloadNotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setAddingNote(false); }
  };

  const removeNote = async (noteId) => {
    if (!window.confirm("Delete this note? Cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/guests/${guestUserId}/notes/${noteId}`);
      toast.success("Note deleted");
      reloadNotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!user) return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-slate-500">Please sign in.</div>;
  if (!user.is_admin) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <XCircle className="mx-auto h-12 w-12 text-rose-500" />
        <h1 className="mt-4 font-display text-2xl font-bold">Admin only</h1>
      </div>
    );
  }

  const s = result?.stats;
  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Guest lookup</h1>
      <p className="mt-1 text-slate-600">
        Search by email, phone or name. See the guest&apos;s account, every booking and every review from one place —
        no more digging through the bookings table.
      </p>

      {/* Search bar */}
      <div className="mt-6 flex flex-wrap gap-2 rounded-2xl bg-white p-3 card-shadow">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
          <input
            data-testid="guest-lookup-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="e.g. lucy@example.com, +260971234567, Bwalya"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
          />
        </div>
        <button onClick={runSearch} disabled={loading} data-testid="guest-lookup-search" className="btn-primary text-sm">
          {loading ? "Searching…" : "Search"}
        </button>
      </div>

      {/* Empty state */}
      {!result && !loading && (
        <div className="mt-10 rounded-3xl bg-white p-12 text-center card-shadow">
          <Search className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-slate-500">Type an email, phone or name above to look up any guest.</p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="mt-8 space-y-6" data-testid="guest-lookup-result">
          {/* Guest card */}
          <div className="rounded-3xl bg-white p-6 card-shadow">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                {result.guest?.picture ? (
                  <img src={result.guest.picture} alt="" className="h-14 w-14 rounded-full object-cover" />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-copper/10 text-copper">
                    <User className="h-6 w-6" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-display text-2xl font-bold text-slate-900">
                      {result.guest?.name || "Unknown guest"}
                    </h2>
                    {result.guest?.is_verified && <BadgeCheck data-testid="guest-verified-badge" className="h-5 w-5 fill-copper text-white" />}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-500">
                    {result.guest?.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {result.guest.email}</span>}
                    {result.guest?.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {result.guest.phone}</span>}
                    {result.guest?.user_id && <span className="font-mono text-[10px] text-slate-400">{result.guest.user_id.slice(0, 12)}…</span>}
                  </div>
                </div>
              </div>
              <div className="text-right text-xs text-slate-500">
                Joined {fmtDate(result.guest?.created_at)}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <Stat icon={Calendar} label="Bookings" value={s.booking_count} />
              <Stat icon={Wallet} label="Paid total" value={money(s.paid_total_zmw)} />
              <Stat icon={Calendar} label="Completed" value={s.completed_stays} />
              <Stat icon={XCircle} label="Cancelled" value={s.cancelled_bookings} />
              <Stat icon={Star} label="Avg rating" value={s.avg_rating ?? "—"} />
            </div>
          </div>

          {/* Admin notes */}
          {guestUserId && (
            <div className="rounded-3xl bg-white p-6 card-shadow" data-testid="admin-notes-section">
              <div className="flex items-center gap-2">
                <StickyNote className="h-5 w-5 text-copper" />
                <h3 className="font-display text-lg font-bold text-slate-900">
                  Private admin notes <span className="ml-1 text-sm font-normal text-slate-500">({notes.length})</span>
                </h3>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Pin VIP flags or troublemaker warnings here. Only visible to admins — never shown to the guest, host, or on any receipt.
              </p>

              <div className="mt-4 flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[240px]">
                  <textarea
                    data-testid="new-note-input"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="e.g. VIP repeat guest — call before check-in. Or: filed chargeback in Aug — require pre-payment."
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
                  />
                </div>
                <button
                  onClick={() => addNote(noteInput)}
                  disabled={addingNote}
                  data-testid="add-note-btn"
                  className="btn-primary text-sm"
                >
                  {addingNote ? "Adding…" : <><Send className="mr-1 inline h-3.5 w-3.5" /> Add note</>}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                Tip: type <b>@admin@easystayzm.com</b> to loop in another admin — they get an email with the note.
              </p>

              {notes.length > 0 && (
                <ThreadedNotes
                  notes={notes}
                  onReply={(parentId, text) => addNote(text, parentId)}
                  onDelete={removeNote}
                  busy={addingNote}
                />
              )}
            </div>
          )}

          {/* Bookings */}
          <div className="rounded-3xl bg-white p-6 card-shadow">
            <h3 className="font-display text-lg font-bold text-slate-900">
              Bookings <span className="ml-1 text-sm font-normal text-slate-500">({result.bookings.length})</span>
            </h3>
            {result.bookings.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">No bookings on record.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-slate-500">
                      <th className="pb-2 pr-4">Property</th>
                      <th className="pb-2 pr-4">Check-in</th>
                      <th className="pb-2 pr-4">Hours</th>
                      <th className="pb-2 pr-4">Total</th>
                      <th className="pb-2 pr-4">Payment</th>
                      <th className="pb-2 pr-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {result.bookings.map((b) => (
                      <tr key={b.id} data-testid={`guest-booking-${b.id}`}>
                        <td className="py-2 pr-4">
                          <Link to={`/property/${b.property_id}`} className="font-medium text-copper hover:underline">
                            {b.property_title || b.property_id}
                          </Link>
                        </td>
                        <td className="py-2 pr-4">{fmtDate(b.check_in)}</td>
                        <td className="py-2 pr-4">{b.duration_hours}h</td>
                        <td className="py-2 pr-4 font-semibold">{money(b.total_zmw)}</td>
                        <td className="py-2 pr-4">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${paymentClass(b.payment_status)}`}>
                            {b.payment_status || "unpaid"}
                          </span>
                        </td>
                        <td className="py-2 pr-4">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${statusClass(b.status)}`}>
                            {b.status || "pending"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Reviews */}
          <div className="rounded-3xl bg-white p-6 card-shadow">
            <h3 className="font-display text-lg font-bold text-slate-900">
              Reviews <span className="ml-1 text-sm font-normal text-slate-500">({result.reviews.length})</span>
            </h3>
            {result.reviews.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">No reviews written yet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {result.reviews.map((r) => (
                  <div key={r.id} data-testid={`guest-review-${r.id}`} className="rounded-xl bg-slate-50 p-4">
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`h-4 w-4 ${n <= (r.rating || 0) ? "fill-copper text-copper" : "text-slate-300"}`} />
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{r.property_title}</span>
                      <span className="ml-auto text-xs text-slate-500">{fmtDate(r.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-700">{r.comment}</p>
                    {r.host_reply && (
                      <div className="mt-3 rounded-lg bg-white p-3 text-xs text-slate-600">
                        <b className="text-slate-900">Host reply:</b> {r.host_reply}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-copper" />
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      </div>
      <div className="mt-1 font-display text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

// Threaded admin notes — root notes newest-first, replies grouped underneath
// in conversational order (oldest-first). Each note has a Reply button that
// expands an inline textarea; replies are POSTed with parent_id set.
function ThreadedNotes({ notes, onReply, onDelete, busy }) {
  const [replyTo, setReplyTo] = React.useState(null);       // note.id or null
  const [replyText, setReplyText] = React.useState("");

  const roots = notes.filter((n) => !n.parent_id);
  const repliesByParent = notes.reduce((acc, n) => {
    if (n.parent_id) (acc[n.parent_id] = acc[n.parent_id] || []).push(n);
    return acc;
  }, {});
  Object.values(repliesByParent).forEach((arr) =>
    arr.sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))
  );

  const submitReply = async (parentId) => {
    await onReply(parentId, replyText);
    setReplyText("");
    setReplyTo(null);
  };

  return (
    <div className="mt-5 space-y-4" data-testid="threaded-notes">
      {roots.map((n) => {
        const replies = repliesByParent[n.id] || [];
        return (
          <div key={n.id} className="rounded-2xl bg-amber-50/60 ring-1 ring-amber-100">
            <NoteBody
              note={n}
              onReplyClick={() => { setReplyTo(replyTo === n.id ? null : n.id); setReplyText(""); }}
              onDelete={onDelete}
            />
            {replies.length > 0 && (
              <div className="border-t border-amber-200/70 bg-white/50 rounded-b-2xl">
                {replies.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`admin-note-reply-${r.id}`}
                    className="flex gap-2 px-4 py-3 border-t border-amber-100 first:border-t-0"
                  >
                    <CornerDownRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                    <div className="flex-1">
                      <NoteBody note={r} onDelete={onDelete} compact />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {replyTo === n.id && (
              <div className="border-t border-amber-200/70 bg-white/70 p-3 rounded-b-2xl">
                <textarea
                  data-testid={`reply-input-${n.id}`}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder="Reply in this thread… mention @teammate@easystayzm.com to loop them in"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-copper"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    onClick={() => { setReplyTo(null); setReplyText(""); }}
                    className="btn-outline text-xs !py-1.5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => submitReply(n.id)}
                    disabled={busy || !replyText.trim()}
                    data-testid={`send-reply-${n.id}`}
                    className="btn-primary text-xs !py-1.5"
                  >
                    <Send className="mr-1 inline h-3 w-3" /> Post reply
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function NoteBody({ note, onReplyClick, onDelete, compact = false }) {
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-ZM", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  return (
    <div className={compact ? "p-1" : "p-4"} data-testid={`admin-note-${note.id}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-800 whitespace-pre-line">{note.note}</p>
        <div className="flex-shrink-0 flex items-center gap-1">
          {onReplyClick && (
            <button
              onClick={onReplyClick}
              data-testid={`reply-note-${note.id}`}
              className="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-copper hover:bg-copper/10"
            >
              Reply
            </button>
          )}
          <button
            onClick={() => onDelete(note.id)}
            data-testid={`delete-note-${note.id}`}
            className="rounded-full p-1.5 text-rose-500 hover:bg-rose-50"
            title="Delete note"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span>{note.author_name} · {fmt(note.created_at)}</span>
        {(note.mentions || []).length > 0 && (
          <span className="flex flex-wrap items-center gap-1">
            {note.mentions.map((m) => (
              <span
                key={m.user_id || m.email}
                data-testid={`mention-${m.email}`}
                className="inline-flex items-center gap-0.5 rounded-full bg-copper/10 px-2 py-0.5 text-[10px] font-semibold text-copper"
              >
                <AtSign className="h-2.5 w-2.5" /> {m.name || m.email}
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}
